import { Button, Spinner, TextInput, Pagination } from 'flowbite-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import RecruiterPositionsTabsComponent from 'src/components/recruiterPositions/RecruiterPositionsTabsComponent';
import { GetDrivePositionsAPIInput, MyPositionType, Position, PositionDocument, PositionStatus } from 'types';
import { useGetAllDrivesQuery, useGetDrivePositionsQuery } from 'src/hooks/drives.hooks';
import NoRecruiterPositionsContainer from 'src/components/recruiterPositions/NoRecruiterPositionsContainer';
import DrivePositionCard from './DrivePositionCard';
import { TbSearch } from 'react-icons/tb';
import { debounce } from 'lodash';
import { revertPositionTitle } from 'shared-utils';
import InviteCollaborator from 'src/components/position/InviteCollaborator';
import PositionsTabsComponent from 'src/components/position/PositionsTabsComponent';
import classNames from 'classnames';
import { useMediaQuery } from '@mui/material';

const DrivePositionsPage = () => {
	const limit = 6;
	const isMobile = useMediaQuery('(max-width:960px)');


	const navigate = useNavigate();
	const { id } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	let tab = searchParams.get('allPosition') === 'true' || !searchParams.has('myPosition') ? "allPosition" : "myPosition";
	let allPosition = searchParams.get('allPosition') === 'true' || !searchParams.has('myPosition');
	let myPosition = searchParams.get('myPosition') === 'true';
	const [active, setActive] = useState<{ open: boolean; closed: boolean }>({
		open: true,
		closed: false
	});

	const [openAddCollaboratorModal, setOpenAddCollaboratorModal] = useState(false);
	useEffect(() => {
		if (!searchParams.has('allPosition') && !searchParams.has('myPosition')) {
			setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.OPEN });
		}
	}, [searchParams, setSearchParams]);
	const { data: allDrivesData, isLoading: isAllDrivesLoading } = useGetAllDrivesQuery();



	const { data: positionData, isLoading } = useGetDrivePositionsQuery({
		data: {
			name: searchParams.get('search') || '',
			...(searchParams.get('allPosition') === 'true'
				? { allPosition: true }
				: searchParams.get('myPosition') === 'true'
					? { myPosition: true }
					: { allPosition: true }), // Default to allPosition if neither is explicitly set
			positionStatus: searchParams.get('positionStatus') || PositionStatus.OPEN,
			companyId: id || '',
			page: +(searchParams.get('page') || '1'),
			limit: limit,
		},
		onComplete: (response) => {
			console.log('Query completed:');
		},
		onSettled: () => {
			console.log('Query settled');
		},
	});

	const allPages = useMemo(() => {
		if (allPosition) {
			return searchParams.get('positionStatus') === PositionStatus.OPEN ? Math.ceil((positionData?.allActivePositionsCount ?? 0) / limit) : Math.ceil((positionData?.allClosedPositionsCount ?? 0) / limit);
		} else if (myPosition) {
			return searchParams.get('positionStatus') === PositionStatus.OPEN ? Math.ceil((positionData?.myActivePositionsCount ?? 0) / limit) : Math.ceil((positionData?.myClosedPositionsCount ?? 0) / limit);
		}
		return 1;
	}, [isLoading, allPosition, myPosition, searchParams, positionData, limit]);



	const toggleActive = () => {
		setActive(prevState => ({ open: !prevState.open, closed: !prevState.closed }));
	};

	const handleSearchInput = (value: string) => {
		const newParams = new URLSearchParams(searchParams);
		newParams.set("search", value);
		setSearchParams(newParams);
	}

	const onChange = (value: string) => {

		if (value === "allPosition") {
			if (active?.open) {
				setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.OPEN });
			}
			if (active?.closed) {
				setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.CLOSED });

			};
			tab=value;

		}

		if (value === "myPosition") {
			if (active?.open) {
				setSearchParams({  positionStatus: PositionStatus.OPEN, myPosition: 'true' });
			}
			if (active?.closed) {
				setSearchParams({ positionStatus: PositionStatus.CLOSED, myPosition: 'true' });
			};
			tab=value;

		}

	};
	return (
		<>
			<div className="flex-1 w-full max-w-[1440px] mx-auto px-4 xl:px-20 flex flex-col">
				<div className="flex flex-col md:flex-row justify-between items-start gap-y-2 md:items-center py-2 pt-6 sm:pt-[10px] border-b border-[#E4E4EB] dark:border-[#555770] border-solid">
					<h4 className="text-xl font-semibold dark:text-white text-[#1C1C28]">Placement Drive Positions</h4>
					<div className="flex gap-[16px] w-full md:w-auto">
						<Button
							size="sm"
							className="whitespace-pre w-full md:w-auto"
							onClick={() => {
								navigate('/upcoming-drives');
							}}
							color="purplePlaced"
							disabled={!allDrivesData?.drives?.length || isAllDrivesLoading}
						>
							Register for New Drive
						</Button>
						<Button
							size="sm"
							className="whitespace-pre w-full md:w-auto"
							onClick={() => {
								setOpenAddCollaboratorModal(true);
							}}
							color="purplePlaced"
						>
							Invite Collaborator
						</Button>
					</div>
				</div>

				<div className="pt-3 flex justify-between flex-wrap items-center gap-3">

					<PositionsTabsComponent
						tab={tab}
						AllpositionsCount={ positionData?.AllpositionsCount||0}
						myPositionsCount={positionData?.myPositionsCount||0}
						onChange={onChange}
					/>

					{/* Search Input */}
					<TextInput
						icon={() => <TbSearch color="#C7C9D9" />}
						className="w-full [&_input]:bg-white max-w-full md:max-w-[522px]"
						placeholder="Search Position"
						name="search"
						value={searchParams.get('search') || ''}
						rightIcon={() => (isLoading && searchParams.get('search') ? <Spinner /> : null)}
						onChange={e => handleSearchInput(e.target.value)}

					/>
				</div>

				<div className='mt-8 px-2 flex justify-between'>
					{allPosition &&
						<div className='flex flex-row gap-[20px] rounded-md'>
							<button
								className={classNames('', {
									'text-blue-500':
										(searchParams.get('positionStatus') === PositionStatus.OPEN && searchParams.get('allPosition') === 'true') || (active.open && searchParams.get('positionStatus') !== PositionStatus.CLOSED)

								})}
								onClick={() => {
									toggleActive();
									setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.OPEN });
								}}
								disabled={(searchParams.get('allPosition') === 'true' && searchParams.get('positionStatus') === PositionStatus.OPEN) || isLoading}
							>
								Active ({positionData?.allActivePositionsCount||0})
							</button>
							<button
								className={classNames('', { 'text-blue-500': searchParams.get('positionStatus') === PositionStatus.CLOSED && !active.open || searchParams.get('positionStatus') === PositionStatus.CLOSED })}
								onClick={() => {
									toggleActive();
									setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.CLOSED });

								}}
								disabled={(searchParams.get('allPosition') === 'true' && searchParams.get('positionStatus') === PositionStatus.CLOSED) || isLoading}
							>
								Closed ({positionData?.allClosedPositionsCount||0})
							</button>
						</div>
					}

					{myPosition &&
						<div className='flex flex-row gap-[20px] rounded-md'>
							<button className={classNames('', { 'text-blue-500': searchParams.get('positionStatus') === PositionStatus.OPEN })}
								onClick={() => {
									toggleActive();
									setSearchParams({ myPosition: 'true', positionStatus: PositionStatus.OPEN });

								}}
								disabled={(searchParams.get('myPosition') === 'true' && searchParams.get('positionStatus') === PositionStatus.OPEN) || isLoading}
							>Active ({positionData?.myActivePositionsCount||0})</button>
							<button className={classNames('', { 'text-blue-500': searchParams.get('positionStatus') === PositionStatus.CLOSED })}
								onClick={() => {
									toggleActive();
									setSearchParams({ myPosition: 'true', positionStatus: PositionStatus.CLOSED });

								}}
								disabled={(searchParams.get('myPosition') === 'true' && searchParams.get('positionStatus') === PositionStatus.CLOSED) || isLoading}
							>Closed ({positionData?.myClosedPositionsCount||0})</button>
						</div>
					}

					{positionData?.positions?.length ? (
						<div>
							<Pagination
								showIcons
								color="purplePlaced"
								className="-mt-2"
								layout={isMobile ? 'navigation' : 'pagination'}
								currentPage={+(searchParams.get('page') || 1)}
								totalPages={allPages}
								onPageChange={(page) => {
									searchParams.set('page', String(page || 1));
									setSearchParams(searchParams);
								}}
							/>
						</div>
					) : null}
				</div>

				<hr className='border border-grey-500 mt-2' />

				{isLoading ? (
					<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="md:!w-[35%] mx-auto" />
				) : positionData?.positions?.length ? (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 py-5">
						{positionData?.positions?.map(position => <DrivePositionCard key={position._id} position={position} />)}
					</div>
				) : (
					<div className="my-auto flex flex-col items-center justify-center">
						<NoRecruiterPositionsContainer
							titleCss="!text-xl !font-semibold"
							title={'No positions found!'}
							description={"You haven't registered for any upcoming drives yet. Register now to view available candidates."}
							buttonText={'Register for New Drive'}
							showAction={true}
							onClick={() => {
								navigate('/upcoming-drives');
							}}
						/>
					</div>
				)}
				{positionData?.positions?.length ? (
					<div className='hidden md:flex justify-center items-center'>
						<Pagination
							showIcons
							color="purplePlaced"
							className="-mt-2"
							layout={isMobile ? 'navigation' : 'pagination'}
							currentPage={+(searchParams.get('page') || 1)}
							totalPages={allPages}
							onPageChange={(page) => {
								searchParams.set('page', String(page || 1));
								setSearchParams(searchParams);
							}}
						/>
					</div>
				) : null}
			</div>
			<InviteCollaborator isOpen={openAddCollaboratorModal} onClose={() => setOpenAddCollaboratorModal(false)} />
		</>
	);
};

export default DrivePositionsPage;